// Per-chart configuration consumed by AnalyticsChart.vue. Adding
// a new chart means appending a spec here — no new component needed.

import { bucketLabel, bucketPoints, type BucketPeriod } from './bucket'
import {
  BAR_BASE,
  LINE_BASE,
  SERIES_COLORS,
  formatCount,
  formatCountShort,
  formatPercent,
  formatUsd,
  formatUsdShort,
  pillLabel,
  type TooltipMatcher,
} from './chartTheme'
import type { AnalyticsKind } from '@/composables/useFCCAnalytics'
import type {
  AuthRateSeriesResponse,
  BinMismatchSeriesResponse,
  ChargebackSeriesResponse,
  LargeTransactionsResponse,
  RefundRateSeriesResponse,
  VolumeSeriesResponse,
} from '@/types/fcc'

export interface ChartData {
  labels: string[]
  // Datasets are passed straight through to chart.js. Strict typing fights the
  // datalabels plugin context — keep loose here, the per-spec builders enforce
  // shape via their literal `type` fields.
  datasets: unknown[]
}

export interface ChartSpec<TResp = unknown> {
  id: string
  title: string
  slug: string
  kind: AnalyticsKind
  buildData: (response: TResp | null) => ChartData
  leftAxisFormat: (value: number) => string
  rightAxis?: { format: (value: number) => string; max?: number }
  tooltipMatchers: TooltipMatcher[]
  leftStacked?: boolean
}

const usdOrCountTooltip: TooltipMatcher = (label, value) =>
  label.includes('USD') ? `${label}: ${formatUsd(value)}` : null
const countTooltip: TooltipMatcher = (label, value) =>
  `${label}: ${formatCount(value)}`
const usdTooltip: TooltipMatcher = (label, value) =>
  `${label}: ${formatUsd(value)}`
const percentTooltip = (matchLabel: string, digits = 2): TooltipMatcher =>
  (label, value) =>
    label === matchLabel ? `${label}: ${formatPercent(value, digits)}` : null

const MONTH: BucketPeriod = 'month'
const WEEK: BucketPeriod = 'week'

const volumeSpec: ChartSpec<VolumeSeriesResponse> = {
  id: 'volume',
  title: 'Transaction Count',
  slug: 'volume',
  kind: 'volume',
  buildData: response => {
    const buckets = bucketPoints(
      response?.points ?? [],
      MONTH,
      () => ({ sell: 0, count: 0 }),
      (acc, p) => ({
        sell: acc.sell + p.sell_amount_usd,
        count: acc.count + p.transaction_count,
      }),
    )
    return {
      labels: buckets.map(b => bucketLabel(b.key, MONTH)),
      datasets: [
        {
          type: 'bar',
          label: 'Sell Amount (USD)',
          data: buckets.map(b => b.agg.sell),
          backgroundColor: SERIES_COLORS.barPrimary,
          borderColor: SERIES_COLORS.barPrimaryBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 2,
          datalabels: { display: false },
        },
        {
          type: 'line',
          label: 'Transaction Count',
          data: buckets.map(b => b.agg.count),
          borderColor: SERIES_COLORS.warmBorder,
          backgroundColor: SERIES_COLORS.warm,
          pointBackgroundColor: SERIES_COLORS.warmBorder,
          pointBorderColor: SERIES_COLORS.warmBorder,
          ...LINE_BASE,
          yAxisID: 'y1',
          order: 1,
          datalabels: pillLabel(formatCountShort, { align: 'bottom' }),
        },
      ],
    }
  },
  leftAxisFormat: formatUsdShort,
  rightAxis: { format: formatCountShort },
  tooltipMatchers: [usdOrCountTooltip, countTooltip],
}

const authRateSpec: ChartSpec<AuthRateSeriesResponse> = {
  id: 'auth-rate',
  title: 'Authorization Rate',
  slug: 'auth-rate',
  kind: 'auth-rate',
  buildData: response => {
    const buckets = bucketPoints(
      response?.points ?? [],
      WEEK,
      () => ({ authorized: 0, notAuthorized: 0 }),
      (acc, p) => ({
        authorized: acc.authorized + p.authorized_count,
        notAuthorized: acc.notAuthorized + p.not_authorized_count,
      }),
    )
    const withRate = buckets.map(b => {
      const total = b.agg.authorized + b.agg.notAuthorized
      return {
        key: b.key,
        authorized: b.agg.authorized,
        notAuthorized: b.agg.notAuthorized,
        rate: total > 0 ? b.agg.authorized / total : 0,
      }
    })
    return {
      labels: withRate.map(b => bucketLabel(b.key, WEEK)),
      datasets: [
        {
          type: 'bar',
          label: 'Authorized Count',
          data: withRate.map(b => b.authorized),
          backgroundColor: SERIES_COLORS.cool,
          borderColor: SERIES_COLORS.coolBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 3,
          datalabels: { display: false },
        },
        {
          type: 'bar',
          label: 'Not Authorized Count',
          data: withRate.map(b => b.notAuthorized),
          backgroundColor: SERIES_COLORS.neutral,
          borderColor: SERIES_COLORS.neutral,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 2,
          datalabels: { display: false },
        },
        {
          type: 'line',
          label: 'Auth Rate',
          data: withRate.map(b => b.rate),
          borderColor: SERIES_COLORS.warmBorder,
          backgroundColor: SERIES_COLORS.warm,
          pointBackgroundColor: SERIES_COLORS.warmBorder,
          pointBorderColor: SERIES_COLORS.warmBorder,
          ...LINE_BASE,
          yAxisID: 'y1',
          order: 1,
          datalabels: pillLabel(v => formatPercent(v, 0), { align: 'bottom' }),
        },
      ],
    }
  },
  leftAxisFormat: formatCountShort,
  rightAxis: { format: v => formatPercent(v, 0), max: 1 },
  tooltipMatchers: [percentTooltip('Auth Rate'), countTooltip],
}

const aovSpec: ChartSpec<VolumeSeriesResponse> = {
  id: 'aov',
  title: 'Avg Order Value',
  slug: 'aov',
  kind: 'volume',
  buildData: response => {
    const buckets = bucketPoints(
      response?.points ?? [],
      MONTH,
      () => ({ sumValue: 0, count: 0 }),
      (acc, p) => ({
        sumValue: acc.sumValue + p.sell_amount_usd,
        count: acc.count + p.transaction_count,
      }),
    )
    return {
      labels: buckets.map(b => bucketLabel(b.key, MONTH)),
      datasets: [
        {
          type: 'bar',
          label: 'Avg Order Value (USD)',
          data: buckets.map(b => (b.agg.count > 0 ? b.agg.sumValue / b.agg.count : 0)),
          backgroundColor: SERIES_COLORS.barPrimary,
          borderColor: SERIES_COLORS.barPrimaryBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 2,
          datalabels: { display: false },
        },
        {
          type: 'line',
          label: 'Transaction Count',
          data: buckets.map(b => b.agg.count),
          borderColor: SERIES_COLORS.warmBorder,
          backgroundColor: SERIES_COLORS.warm,
          pointBackgroundColor: SERIES_COLORS.warmBorder,
          pointBorderColor: SERIES_COLORS.warmBorder,
          ...LINE_BASE,
          yAxisID: 'y1',
          order: 1,
          datalabels: pillLabel(formatCountShort, { align: 'bottom' }),
        },
      ],
    }
  },
  leftAxisFormat: formatUsdShort,
  rightAxis: { format: formatCountShort },
  tooltipMatchers: [usdOrCountTooltip, countTooltip],
}

const refundRateSpec: ChartSpec<RefundRateSeriesResponse> = {
  id: 'refund-rate',
  title: 'Refund Analysis',
  slug: 'refund-rate',
  kind: 'refund-rate',
  buildData: response => {
    const buckets = bucketPoints(
      response?.points ?? [],
      MONTH,
      () => ({ refundSell: 0, grossSell: 0 }),
      (acc, p) => ({
        refundSell: acc.refundSell + p.refund_sell_amount_usd,
        grossSell: acc.grossSell + p.gross_sell_amount_usd,
      }),
    )
    const withRate = buckets.map(b => ({
      key: b.key,
      refundSell: b.agg.refundSell,
      grossSell: b.agg.grossSell,
      rate: b.agg.grossSell > 0 ? b.agg.refundSell / b.agg.grossSell : 0,
    }))
    return {
      labels: withRate.map(b => bucketLabel(b.key, MONTH)),
      datasets: [
        {
          type: 'bar',
          label: 'Gross Sell (USD)',
          data: withRate.map(b => b.grossSell),
          backgroundColor: SERIES_COLORS.barPrimary,
          borderColor: SERIES_COLORS.barPrimaryBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 4,
          datalabels: { display: false },
        },
        {
          type: 'bar',
          label: 'Refund Sell (USD)',
          data: withRate.map(b => b.refundSell),
          backgroundColor: SERIES_COLORS.warm,
          borderColor: SERIES_COLORS.warmBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 3,
          datalabels: { display: false },
        },
        {
          type: 'line',
          label: 'Refund Rate',
          data: withRate.map(b => b.rate),
          borderColor: SERIES_COLORS.coolBorder,
          backgroundColor: SERIES_COLORS.cool,
          pointBackgroundColor: SERIES_COLORS.coolBorder,
          pointBorderColor: SERIES_COLORS.coolBorder,
          ...LINE_BASE,
          yAxisID: 'y1',
          order: 1,
          datalabels: pillLabel(v => formatPercent(v, 2), { align: 'bottom' }),
        },
      ],
    }
  },
  leftAxisFormat: formatUsdShort,
  rightAxis: { format: v => formatPercent(v, 0), max: 1 },
  tooltipMatchers: [percentTooltip('Refund Rate'), usdTooltip],
}

const largeTransactionsSpec: ChartSpec<LargeTransactionsResponse> = {
  id: 'large-transactions',
  title: 'Large Transactions',
  slug: 'large-transactions',
  kind: 'large-transactions',
  buildData: response => {
    const points = [...(response?.daily_max ?? [])].sort((a, b) =>
      a.date.localeCompare(b.date),
    )
    return {
      labels: points.map(p => p.date),
      datasets: [
        {
          type: 'bar',
          label: 'Daily Max (USD)',
          data: points.map(p => p.daily_max_txn_usd),
          backgroundColor: SERIES_COLORS.barPrimary,
          borderColor: SERIES_COLORS.barPrimaryBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          datalabels: pillLabel(formatUsdShort, { align: 'top', anchor: 'end' }),
        },
      ],
    }
  },
  leftAxisFormat: formatUsdShort,
  tooltipMatchers: [usdTooltip],
}

const chargebacksSpec: ChartSpec<ChargebackSeriesResponse> = {
  id: 'chargebacks',
  title: 'Chargeback Analysis',
  slug: 'chargebacks',
  kind: 'chargebacks',
  buildData: response => {
    const buckets = bucketPoints(
      response?.points ?? [],
      MONTH,
      () => ({ amount: 0, count: 0 }),
      (acc, p) => ({
        amount: acc.amount + p.chargeback_amount_usd,
        count: acc.count + p.chargeback_count,
      }),
    )
    return {
      labels: buckets.map(b => bucketLabel(b.key, MONTH)),
      datasets: [
        {
          type: 'bar',
          label: 'CB Amount (USD)',
          data: buckets.map(b => b.agg.amount),
          backgroundColor: SERIES_COLORS.barPrimary,
          borderColor: SERIES_COLORS.barPrimaryBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 2,
          datalabels: { display: false },
        },
        {
          type: 'line',
          label: 'CB Count',
          data: buckets.map(b => b.agg.count),
          borderColor: SERIES_COLORS.warmBorder,
          backgroundColor: SERIES_COLORS.warm,
          pointBackgroundColor: SERIES_COLORS.warmBorder,
          pointBorderColor: SERIES_COLORS.warmBorder,
          ...LINE_BASE,
          yAxisID: 'y1',
          order: 1,
          datalabels: pillLabel(formatCount, { align: 'bottom' }),
        },
      ],
    }
  },
  leftAxisFormat: formatUsdShort,
  rightAxis: { format: formatCountShort },
  tooltipMatchers: [usdOrCountTooltip, countTooltip],
}

const binMismatchSpec: ChartSpec<BinMismatchSeriesResponse> = {
  id: 'bin-mismatch',
  title: 'BIN Mismatch',
  slug: 'bin-mismatch',
  kind: 'bin-mismatch',
  leftStacked: true,
  buildData: response => {
    const buckets = bucketPoints(
      response?.points ?? [],
      MONTH,
      () => ({ match: 0, mismatch: 0 }),
      (acc, p) => ({
        match: acc.match + p.bin_match_count,
        mismatch: acc.mismatch + p.bin_mismatch_count,
      }),
    )
    return {
      labels: buckets.map(b => bucketLabel(b.key, MONTH)),
      datasets: [
        {
          type: 'bar',
          label: 'BIN Match',
          data: buckets.map(b => b.agg.match),
          backgroundColor: SERIES_COLORS.good,
          borderColor: SERIES_COLORS.goodBorder,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 3,
          stack: 'bin',
          datalabels: { display: false },
        },
        {
          type: 'bar',
          label: 'BIN Mismatch',
          data: buckets.map(b => b.agg.mismatch),
          backgroundColor: SERIES_COLORS.neutral,
          borderColor: SERIES_COLORS.neutral,
          ...BAR_BASE,
          yAxisID: 'y',
          order: 2,
          stack: 'bin',
          datalabels: { display: false },
        },
        {
          type: 'line',
          label: 'Mismatch Rate',
          data: buckets.map(b => {
            const total = b.agg.match + b.agg.mismatch
            return total > 0 ? b.agg.mismatch / total : 0
          }),
          borderColor: SERIES_COLORS.warmBorder,
          backgroundColor: SERIES_COLORS.warm,
          pointBackgroundColor: SERIES_COLORS.warmBorder,
          pointBorderColor: SERIES_COLORS.warmBorder,
          ...LINE_BASE,
          yAxisID: 'y1',
          order: 1,
          datalabels: pillLabel(v => formatPercent(v, 1), { align: 'bottom' }),
        },
      ],
    }
  },
  leftAxisFormat: formatCountShort,
  rightAxis: { format: v => formatPercent(v, 0), max: 1 },
  tooltipMatchers: [percentTooltip('Mismatch Rate'), countTooltip],
}

export const CHART_SPECS: ReadonlyArray<ChartSpec> = [
  volumeSpec,
  authRateSpec,
  aovSpec,
  refundRateSpec,
  largeTransactionsSpec,
  chargebacksSpec,
  binMismatchSpec,
] as ReadonlyArray<ChartSpec>
